import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Search, Image as ImageIcon, Refresh, Store } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from './constants';
import dayjs from 'dayjs';
import { getDefaultCountryForProject, getDefaultManufacturerRetailers } from './utils/brandBoardUtils';
import { mergeJtiProductRowOverrides } from './productUtils';

const BRAND_HEADER_CONFIG = {
  IQOS: { label: 'iqos / iluma', color: '#1aa97c' },
  Glo: { label: 'glo', color: '#bcd9d2' },
  Ploom: { label: 'ploom', color: '#0a2a3b' }
};

function BrandBoardSummaryStyledView() {
  const { projectKey } = useParams();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedCountry, setSelectedCountry] = useState('ro');
  const [availableCountries, setAvailableCountries] = useState([]);
  const [selectedRetailers, setSelectedRetailers] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [searchTerm, setSearchTerm] = useState('');

  const [filterOptions, setFilterOptions] = useState({ retailers: [], brands: [] });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetailDialogOpen, setProductDetailDialogOpen] = useState(false);

  const defaultManufacturerRetailersAppliedRef = useRef(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (defaultManufacturerRetailersAppliedRef.current || selectedRetailers.length > 0) return;
    const r = filterOptions.retailers || [];
    if (!r.length) return;
    const mfr = getDefaultManufacturerRetailers(r);
    defaultManufacturerRetailersAppliedRef.current = true;
    if (mfr.length > 0) setSelectedRetailers(mfr);
  }, [filterOptions.retailers, selectedRetailers.length]);

  useEffect(() => {
    applyFilters();
  }, [data, selectedCountry, selectedRetailers, selectedBrands, dateRange, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [dataResponse, overridesResponse, brandOverridesResponse] = await Promise.all([
        axios.get(`${API_URL}/jti/global-merged-data`),
        axios.get(`${API_URL}/jti/product-category-overrides`).catch(() => ({ data: { overrides: {} } })),
        axios.get(`${API_URL}/jti/product-brand-overrides`).catch(() => ({ data: { overrides: {} } })),
      ]);
      if (dataResponse.data && dataResponse.data.data) {
        const { data: rawData, columns } = dataResponse.data;
        const formattedData = rawData.map((row) => {
          const obj = {};
          columns.forEach((col, index) => {
            obj[col] = row[index];
          });
          return obj;
        });
        const overrides = overridesResponse?.data?.overrides || {};
        const brandOverrides = brandOverridesResponse?.data?.overrides || {};
        const formattedWithOverrides = formattedData.map((item) =>
          mergeJtiProductRowOverrides(item, overrides, brandOverrides)
        );
        setData(formattedWithOverrides);
        extractFilterOptions(formattedWithOverrides);

        const dates = formattedWithOverrides
          .map((item) => item.updated_at || item.date)
          .filter(Boolean)
          .map((value) => dayjs(value))
          .filter((value) => value.isValid());
        if (dates.length > 0) {
          const minDate = dates.reduce((min, current) => (current.isBefore(min) ? current : min), dates[0]);
          const maxDate = dates.reduce((max, current) => (current.isAfter(max) ? current : max), dates[0]);
          setDateRange({ start: minDate, end: maxDate });
        }
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load brand board summary data');
    } finally {
      setLoading(false);
    }
  };

  const extractFilterOptions = (rows) => {
    const countries = [...new Set(rows.map((item) => item.country).filter(Boolean))];
    const retailers = [...new Set(rows.map((item) => item.retailer).filter(Boolean))];
    const brands = [...new Set(rows.map((item) => item.brand).filter(Boolean))];

    setAvailableCountries(countries);
    setFilterOptions({ retailers, brands });
    const defaultCountry = getDefaultCountryForProject(projectKey, countries);
    if (defaultCountry) {
      setSelectedCountry(defaultCountry);
    } else if (countries.length > 0 && !countries.includes(selectedCountry)) {
      setSelectedCountry(countries[0]);
    }
  };

  const applyFilters = () => {
    let filtered = [...data];
    if (selectedCountry) {
      filtered = filtered.filter((item) => item.country === selectedCountry);
    }
    if (selectedRetailers.length > 0) {
      filtered = filtered.filter((item) => selectedRetailers.includes(item.retailer));
    }
    if (selectedBrands.length > 0) {
      filtered = filtered.filter((item) => selectedBrands.includes(item.brand));
    }
    if (dateRange.start && dateRange.end) {
      filtered = filtered.filter((item) => {
        const rawDate = item.updated_at || item.date;
        if (!rawDate) return true;
        const itemDate = dayjs(rawDate);
        if (!itemDate.isValid()) return true;
        return itemDate.isAfter(dateRange.start.subtract(1, 'day')) &&
          itemDate.isBefore(dateRange.end.add(1, 'day'));
      });
    }
    if (searchTerm) {
      filtered = filtered.filter((item) =>
        (item.title && item.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.brand && item.brand.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    setFilteredData(filtered);
  };

  const convertPrice = (price, currency) => {
    if (!price || isNaN(parseFloat(price))) return { price: 0, currency: 'EUR' };
    const numericPrice = parseFloat(price);
    if (currency === 'RON') {
      return { price: (numericPrice / 5.09).toFixed(2), currency: 'EUR' };
    }
    if (currency === 'GBP') {
      return { price: (numericPrice * 1.15).toFixed(2), currency: 'EUR' };
    }
    return { price: numericPrice.toFixed(2), currency: currency || 'EUR' };
  };

  const deduplicateProducts = (products) => {
    const productMap = new Map();
    products.forEach((product) => {
      const baseTitle = (product.title || '').toLowerCase()
        .replace(/\s+(menthol|mint|tobacco|original|classic|smooth|fresh|cool|warm|hot|mild|strong|light|dark|black|white|red|blue|green|yellow|orange|purple|pink|brown|grey|gray|silver|gold|platinum|premium|deluxe|special|limited|edition|flavor|taste|aroma|scent|smell|fragrance)\s*/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      const brand = (product.brand || '').toLowerCase().trim();
      const retailer = (product.retailer || '').toLowerCase().trim();
      const category = (product.clean_category || '').toLowerCase().trim();
      const key = `${baseTitle}_${brand}_${retailer}_${category}`;
      if (!productMap.has(key)) {
        productMap.set(key, product);
      }
    });
    return Array.from(productMap.values());
  };

  const createBrandBoardLayout = (products) => {
    const deduplicatedProducts = deduplicateProducts(products);
    const productsWithConvertedPrices = deduplicatedProducts.map((product) => {
      const priceData = convertPrice(product.actual_price || product.regular_price, product.currency);
      return {
        ...product,
        convertedPrice: parseFloat(priceData.price),
        displayPrice: priceData.price,
        displayCurrency: priceData.currency
      };
    });

    if (productsWithConvertedPrices.length === 0) {
      return { priceGroups: [], allBrands: [] };
    }

    let referencePrice = productsWithConvertedPrices[0].convertedPrice || 1;
    const productsWithPriceIndex = productsWithConvertedPrices.map((product) => {
      const priceIndex = Math.round((product.convertedPrice / referencePrice) * 100);
      return { ...product, priceIndex };
    });

    const priceGroups = [];
    productsWithPriceIndex.sort((a, b) => b.priceIndex - a.priceIndex);
    const tolerance = 5;
    productsWithPriceIndex.forEach((product) => {
      let added = false;
      for (const group of priceGroups) {
        const groupAvg = group.products.reduce((sum, p) => sum + p.priceIndex, 0) / group.products.length;
        if (Math.abs(product.priceIndex - groupAvg) <= tolerance) {
          group.products.push(product);
          added = true;
          break;
        }
      }
      if (!added) {
        priceGroups.push({ priceIndex: product.priceIndex, products: [product] });
      }
    });

    const allBrands = [...new Set(productsWithPriceIndex.map((p) => p.brand))].filter(Boolean);

    return { priceGroups, allBrands };
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setProductDetailDialogOpen(true);
  };

  const renderBrandBoardLayout = () => {
    const layout = createBrandBoardLayout(filteredData);
    const { priceGroups, allBrands } = layout;

    if (!allBrands.length) {
      return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="body2" color="text.secondary">
            No products found for the current filters.
          </Typography>
        </Box>
      );
    }

    const orderedBrands = ['IQOS', 'Glo', 'Ploom'];
    const displayBrands = [
      ...orderedBrands.filter((brand) => allBrands.includes(brand)),
      ...allBrands.filter((brand) => !orderedBrands.includes(brand))
    ];

    return (
      <Paper sx={{ p: 2, backgroundColor: '#f6f6f6', border: '1px solid #d9d9d9' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, letterSpacing: 1 }}>
          HT DEVICES BRAND BOARD
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
          <Box sx={{ width: 80 }} />
          {displayBrands.map((brand) => {
            const header = BRAND_HEADER_CONFIG[brand] || { label: brand.toLowerCase(), color: '#7d7d7d' };
            return (
              <Box
                key={brand}
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  backgroundColor: header.color,
                  color: brand === 'Ploom' ? '#fff' : '#0a0a0a',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  py: 0.5,
                  borderRadius: 0.5,
                  mx: 0.5
                }}
              >
                {header.label}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ mt: 1 }}>
          {priceGroups.map((group, index) => (
            <Box
              key={`${group.priceIndex}-${index}`}
              sx={{
                display: 'flex',
                borderBottom: '1px solid #d9d9d9',
                minHeight: 70,
                alignItems: 'stretch'
              }}
            >
              <Box
                sx={{
                  width: 80,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  color: '#444'
                }}
              >
                {group.priceIndex}%
              </Box>
              {displayBrands.map((brand) => {
                const brandProducts = group.products.filter((product) => product.brand === brand).slice(0, 2);
                return (
                  <Box
                    key={`${group.priceIndex}-${brand}`}
                    sx={{
                      flex: 1,
                      px: 1,
                      py: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      backgroundColor: '#f9f9f9',
                      borderLeft: '1px solid #ededed'
                    }}
                  >
                    {brandProducts.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    ) : (
                      brandProducts.map((product) => (
                        <Box
                          key={product.id || product.title}
                          onClick={() => handleProductClick(product)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'pointer'
                          }}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 0.5,
                              backgroundColor: '#fff',
                              border: '1px solid #e0e0e0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden'
                            }}
                          >
                            {product.image && product.image !== 'null' ? (
                              <img
                                src={product.image}
                                alt={product.title}
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                              />
                            ) : (
                              <ImageIcon sx={{ fontSize: 18, color: '#9e9e9e' }} />
                            )}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                              {product.title || 'Product'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {product.displayCurrency === 'EUR' ? `€${product.displayPrice}` : `${product.displayPrice} ${product.displayCurrency}`}
                            </Typography>
                          </Box>
                        </Box>
                      ))
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Paper>
    );
  };

  const deduplicatedProducts = useMemo(() => deduplicateProducts(filteredData), [filteredData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading brand board summary...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={loadData} sx={{ mt: 2 }}>
          <Refresh sx={{ mr: 1 }} />
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Store sx={{ fontSize: 22 }} />
          Brand Board Summary (Styled)
        </Typography>
        <Chip label={`${selectedCountry.toUpperCase()} · ${deduplicatedProducts.length} products`} size="small" />
      </Box>

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Country</InputLabel>
                <Select
                  value={selectedCountry}
                  label="Country"
                  onChange={(e) => setSelectedCountry(e.target.value)}
                >
                  {availableCountries.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Retailer</InputLabel>
                <Select
                  multiple
                  label="Retailer"
                  value={selectedRetailers}
                  onChange={(e) => setSelectedRetailers(e.target.value)}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {filterOptions.retailers.map((retailer) => (
                    <MenuItem key={retailer} value={retailer}>
                      {retailer}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Brand</InputLabel>
                <Select
                  multiple
                  label="Brand"
                  value={selectedBrands}
                  onChange={(e) => setSelectedBrands(e.target.value)}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {filterOptions.brands.map((brand) => (
                    <MenuItem key={brand} value={brand}>
                      {brand}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {renderBrandBoardLayout()}

      <Dialog
        open={productDetailDialogOpen}
        onClose={() => setProductDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Product Details</DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedProduct.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedProduct.brand} · {selectedProduct.retailer}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {selectedProduct.displayCurrency === 'EUR'
                  ? `€${selectedProduct.displayPrice}`
                  : `${selectedProduct.displayPrice} ${selectedProduct.displayCurrency}`}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BrandBoardSummaryStyledView;
